/**
 * Реестр индикаторов правил.
 *
 * Одно описание — три потребителя: конструктор строит по нему списки и
 * поля параметров, вычислитель считает значения, Core считает то же
 * самое при закрытом терминале. Список того, что человек может выбрать,
 * и список того, что платформа умеет считать, обязаны совпадать:
 * величина, которую можно выбрать, но нельзя посчитать, — худший вид
 * неисправности, потому что правило молчит без объяснения.
 *
 * Реестр живёт здесь, а не в терминале, по той же причине, по которой
 * здесь живёт математика: уведомление должно приходить при закрытой
 * вкладке, значит правило считает Core. Два реестра означали бы два
 * ответа на вопрос «что показывал RSI в этот момент».
 *
 * Незнакомый индикатор возвращает пустой ряд, а не бросает исключение:
 * в профиле может лежать правило, ссылающееся на то, чего в списке
 * больше нет. Пустой ряд означает «значения нет», а условие с
 * отсутствующим значением не выполняется — правило молчит, вместо того
 * чтобы врать.
 */
import { atr, ema, rollingStdDev, rsi, sma } from './math.js';
import { fairValueGaps, orderBlocks, premium, structure, sweeps, swings, } from './structure.js';
import { doji, engulfing, insideBar, pinBar } from './candles.js';
import { divergences } from './divergence.js';
import { aroon, donchian, ichimoku, keltner, mfi, parabolicSar, pivots, stars, threeSoldiers, williamsR } from './ta-extra.js';
import { scriptSeries } from './script.js';
import { adx, cci, macd, obv, relativeVolume, roc, stochasticK, supertrend, vwap, wma, } from './ta.js';
const PERIOD = (value, label = 'Период', max = 500) => ({
    key: 'period',
    label,
    default: value,
    min: 1,
    max,
});
/**
 * Список индикаторов.
 *
 * Порядок внутри категории — от самого частого к редкому: человек,
 * открывший список впервые, должен увидеть знакомое в первых строках.
 */
export const RULE_INDICATORS = [
    // --- тренд
    {
        id: 'ema',
        label: 'EMA',
        category: 'trend',
        hint: 'Скользящее среднее с упором на свежие бары',
        params: [PERIOD(20)],
    },
    {
        id: 'sma',
        label: 'SMA',
        category: 'trend',
        hint: 'Простое среднее за период',
        params: [PERIOD(20)],
    },
    {
        id: 'wma',
        label: 'WMA',
        category: 'trend',
        hint: 'Среднее с линейным весом: свежее важнее',
        params: [PERIOD(20)],
    },
    {
        id: 'vwap',
        label: 'VWAP',
        category: 'trend',
        hint: 'Средняя цена с учётом объёма',
        params: [PERIOD(20)],
    },
    {
        id: 'supertrend',
        label: 'Supertrend',
        category: 'trend',
        hint: 'Линия, которая переворачивается при пробое',
        params: [
            PERIOD(10, 'Период ATR', 200),
            { key: 'multiplier', label: 'Множитель', default: 3, min: 0.5, max: 10, step: 0.1 },
        ],
    },
    {
        id: 'adx',
        label: 'ADX',
        category: 'trend',
        hint: 'Сила тренда без направления: высокий и на падении',
        params: [PERIOD(14)],
    },
    {
        id: 'ichimoku',
        label: 'Облако Ишимоку',
        category: 'trend',
        hint: 'Над облаком — тренд вверх, под ним — вниз, внутри — неясно',
        params: [
            { key: 'tenkan', label: 'Тенкан', default: 9, min: 1, max: 200 },
            { key: 'kijun', label: 'Киджун', default: 26, min: 2, max: 300 },
            { key: 'senkou', label: 'Сенкоу B', default: 52, min: 2, max: 500 },
        ],
        outputs: [
            { key: 'cloud_top', label: 'Верх облака' },
            { key: 'cloud_bottom', label: 'Низ облака' },
            { key: 'tenkan', label: 'Тенкан' },
            { key: 'kijun', label: 'Киджун' },
            { key: 'span_a', label: 'Сенкоу A' },
            { key: 'span_b', label: 'Сенкоу B' },
        ],
    },
    {
        id: 'psar',
        label: 'Параболик SAR',
        category: 'trend',
        hint: 'Точки-стопы: под ценой — тренд вверх, над ценой — вниз',
        params: [
            { key: 'step', label: 'Шаг ускорения', default: 0.02, min: 0.001, max: 0.2, step: 0.001 },
            { key: 'max', label: 'Наибольшее ускорение', default: 0.2, min: 0.01, max: 1, step: 0.01 },
        ],
    },
    {
        id: 'aroon',
        label: 'Aroon',
        category: 'trend',
        hint: 'Как давно был максимум и минимум: 100 — только что, 0 — давно',
        params: [PERIOD(25)],
        outputs: [
            { key: 'up', label: 'Вверх' },
            { key: 'down', label: 'Вниз' },
            { key: 'oscillator', label: 'Разница' },
        ],
    },
    // --- импульс
    {
        id: 'rsi',
        label: 'RSI',
        category: 'momentum',
        hint: 'Перекупленность и перепроданность, 0–100',
        params: [PERIOD(14)],
    },
    {
        id: 'macd',
        label: 'MACD',
        category: 'momentum',
        hint: 'Расхождение двух средних и сигнальная линия',
        params: [
            { key: 'fast', label: 'Быстрая', default: 12, min: 1, max: 200 },
            { key: 'slow', label: 'Медленная', default: 26, min: 2, max: 400 },
            { key: 'signal', label: 'Сигнальная', default: 9, min: 1, max: 200 },
        ],
        outputs: [
            { key: 'line', label: 'Линия' },
            { key: 'signal', label: 'Сигнальная' },
            { key: 'histogram', label: 'Гистограмма' },
        ],
    },
    {
        id: 'stochastic',
        label: 'Stochastic',
        category: 'momentum',
        hint: 'Где закрытие внутри диапазона периода',
        params: [PERIOD(14), { key: 'smooth', label: 'Сглаживание %D', default: 3, min: 1, max: 50 }],
        outputs: [
            { key: 'k', label: '%K' },
            { key: 'd', label: '%D' },
        ],
    },
    {
        id: 'cci',
        label: 'CCI',
        category: 'momentum',
        hint: 'Отклонение от средней, обычно в пределах ±100',
        params: [PERIOD(20)],
    },
    {
        id: 'roc',
        label: 'Скорость изменения',
        category: 'momentum',
        hint: 'На сколько процентов изменилась цена за период',
        params: [PERIOD(9)],
    },
    {
        id: 'williams_r',
        label: 'Williams %R',
        category: 'momentum',
        hint: 'Где закрытие в диапазоне: −100 у дна, 0 у вершины; ниже −80 — перепродан',
        params: [PERIOD(14)],
    },
    // --- волатильность
    {
        id: 'atr',
        label: 'ATR',
        category: 'volatility',
        hint: 'Средний размах бара: мера обычного движения',
        params: [PERIOD(14)],
    },
    {
        id: 'bollinger',
        label: 'Полосы Боллинджера',
        category: 'volatility',
        hint: 'Канал из средней и отклонения',
        params: [
            PERIOD(20),
            { key: 'deviations', label: 'Отклонений', default: 2, min: 0.5, max: 5, step: 0.1 },
        ],
        outputs: [
            { key: 'upper', label: 'Верхняя' },
            { key: 'middle', label: 'Средняя' },
            { key: 'lower', label: 'Нижняя' },
        ],
    },
    {
        id: 'stddev',
        label: 'Отклонение',
        category: 'volatility',
        hint: 'Насколько цена разбросана вокруг средней',
        params: [PERIOD(20)],
    },
    {
        id: 'keltner',
        label: 'Канал Кельтнера',
        category: 'volatility',
        hint: 'EMA ± несколько средних ходов свечи: спокойнее полос Боллинджера',
        params: [
            PERIOD(20),
            { key: 'multiplier', label: 'Множитель ATR', default: 2, min: 0.5, max: 5, step: 0.1 },
            { key: 'atrPeriod', label: 'Период ATR', default: 10, min: 1, max: 200 },
        ],
        outputs: [
            { key: 'upper', label: 'Верхняя' },
            { key: 'middle', label: 'Средняя' },
            { key: 'lower', label: 'Нижняя' },
        ],
    },
    {
        id: 'donchian',
        label: 'Канал Дончиана',
        category: 'volatility',
        hint: 'Максимум и минимум прошлых свечей: выход за канал — пробой, как у «черепах»',
        params: [PERIOD(20)],
        outputs: [
            { key: 'upper', label: 'Верхняя' },
            { key: 'middle', label: 'Средняя' },
            { key: 'lower', label: 'Нижняя' },
        ],
    },
    // --- объём
    {
        id: 'volume_sma',
        label: 'Средний объём',
        category: 'volume',
        hint: 'Обычный объём за период — с чем сравнивать текущий',
        params: [PERIOD(20)],
    },
    {
        id: 'relative_volume',
        label: 'Относительный объём',
        category: 'volume',
        hint: 'Во сколько раз больше обычного: 1 — как всегда',
        params: [PERIOD(20)],
    },
    {
        id: 'obv',
        label: 'OBV',
        category: 'volume',
        hint: 'Накопленный объём со знаком: куда идут деньги',
        params: [],
    },
    {
        id: 'mfi',
        label: 'MFI',
        category: 'volume',
        hint: 'RSI с учётом объёма, 0–100: ниже 20 — деньги уходят, выше 80 — перегрев',
        params: [PERIOD(14)],
    },
    /*
     * Spot CVD — не из свечей графика.
     *
     * Перевес покупок над продажами «по рынку» отдаёт только спот Binance,
     * и терминал с Core вписывают его в бары перед расчётом. Выше нуля —
     * покупают настойчивее, чем продают; ниже — наоборот. Первый выход —
     * сумма за период: «за последние двадцать баров покупали больше».
     */
    {
        id: 'spot_cvd',
        label: 'Spot CVD',
        category: 'volume',
        hint: 'Покупки минус продажи «по рынку» на споте Binance: выше нуля — покупают настойчивее',
        params: [{ key: 'period', label: 'За сколько баров', default: 20, min: 1, max: 500, unit: 'баров' }],
        outputs: [
            { key: 'sum', label: 'Перевес за период' },
            { key: 'delta', label: 'Перевес за бар' },
        ],
    },
    /*
     * Futures CVD — то же в бессрочных контрактах Binance.
     *
     * Выше нуля — ставят на рост настойчивее, чем на падение. Сам по себе
     * он говорит половину; сильнее всего он рядом со Spot CVD: цена идёт за
     * контрактами, а монеты на споте — в обратную сторону.
     */
    {
        id: 'futures_cvd',
        label: 'Futures CVD',
        category: 'volume',
        hint: 'Покупки минус продажи «по рынку» в контрактах Binance: выше нуля — ставят на рост настойчивее',
        params: [{ key: 'period', label: 'За сколько баров', default: 20, min: 1, max: 500, unit: 'баров' }],
        outputs: [
            { key: 'sum', label: 'Перевес за период' },
            { key: 'delta', label: 'Перевес за бар' },
        ],
    },
    /* ------------------------------------------------- структура рынка */
    {
        id: 'swing',
        label: 'Экстремумы',
        category: 'structure',
        hint: 'Последняя подтверждённая вершина и впадина',
        params: [
            { key: 'left', label: 'Баров слева', default: 5, min: 1, max: 100, unit: 'баров' },
            { key: 'right', label: 'Баров справа', default: 5, min: 1, max: 100, unit: 'баров' },
        ],
        outputs: [
            { key: 'high', label: 'Вершина' },
            { key: 'low', label: 'Впадина' },
        ],
    },
    {
        id: 'bos',
        label: 'Пробой структуры',
        category: 'structure',
        hint: 'Цена ушла за прошлый экстремум по ходу тренда: 1 в момент пробоя',
        params: [
            { key: 'left', label: 'Баров слева', default: 5, min: 1, max: 100, unit: 'баров' },
            { key: 'right', label: 'Баров справа', default: 5, min: 1, max: 100, unit: 'баров' },
        ],
        outputs: [
            { key: 'up', label: 'Вверх', flag: true },
            { key: 'down', label: 'Вниз', flag: true },
        ],
    },
    {
        id: 'choch',
        label: 'Слом характера',
        category: 'structure',
        hint: 'Первый пробой против тренда — рынок собрался делать другое',
        params: [
            { key: 'left', label: 'Баров слева', default: 5, min: 1, max: 100, unit: 'баров' },
            { key: 'right', label: 'Баров справа', default: 5, min: 1, max: 100, unit: 'баров' },
        ],
        outputs: [
            { key: 'up', label: 'Вверх', flag: true },
            { key: 'down', label: 'Вниз', flag: true },
        ],
    },
    {
        id: 'divergence',
        label: 'Дивергенция',
        category: 'structure',
        hint: 'Цена ставит новый экстремум, импульс — нет: 1 в момент подтверждения',
        params: [
            { key: 'period', label: 'Период RSI', default: 14, min: 2, max: 100, unit: 'баров' },
            { key: 'left', label: 'Баров слева', default: 5, min: 1, max: 100, unit: 'баров' },
            { key: 'right', label: 'Баров справа', default: 5, min: 1, max: 100, unit: 'баров' },
            {
                /*
                 * Предел нужен, чтобы расхождение относилось к текущему
                 * движению. Вершина месячной давности формально сравнится с
                 * сегодняшней, но общего у них уже нет.
                 */
                key: 'maxGap',
                label: 'Не дальше, чем',
                default: 60,
                min: 0,
                max: 500,
                unit: 'баров',
            },
        ],
        outputs: [
            { key: 'up', label: 'Бычья', flag: true },
            { key: 'down', label: 'Медвежья', flag: true },
            /*
             * Скрытая — не разворот, а передышка внутри тренда, и вход по ней
             * идёт в другую сторону. Разными выходами, а не настройкой:
             * галочка «скрытая» меняла бы смысл сигнала молча.
             */
            { key: 'hidden_up', label: 'Скрытая бычья', flag: true },
            { key: 'hidden_down', label: 'Скрытая медвежья', flag: true },
            { key: 'triple_up', label: 'Тройная бычья', flag: true },
            { key: 'triple_down', label: 'Тройная медвежья', flag: true },
        ],
    },
    {
        id: 'trend_structure',
        label: 'Направление структуры',
        category: 'structure',
        hint: '1 — структура вверх, −1 — вниз, 0 — пока неясно',
        values: [
            { value: 1, label: 'вверх' },
            { value: -1, label: 'вниз' },
            { value: 0, label: 'пока неясно' },
        ],
        params: [
            { key: 'left', label: 'Баров слева', default: 5, min: 1, max: 100, unit: 'баров' },
            { key: 'right', label: 'Баров справа', default: 5, min: 1, max: 100, unit: 'баров' },
        ],
    },
    {
        id: 'sweep',
        label: 'Снятие ликвидности',
        category: 'liquidity',
        hint: 'Тень зашла за уровень, закрытие вернулось: стопы собраны',
        params: [
            { key: 'left', label: 'Баров слева', default: 5, min: 1, max: 100, unit: 'баров' },
            { key: 'right', label: 'Баров справа', default: 5, min: 1, max: 100, unit: 'баров' },
        ],
        outputs: [
            { key: 'high', label: 'Сверху', flag: true },
            { key: 'low', label: 'Снизу', flag: true },
        ],
    },
    {
        id: 'fvg',
        label: 'Разрыв цены (FVG)',
        category: 'zone',
        hint: 'Промежуток, который рынок проскочил и обычно возвращается закрыть',
        params: [
            {
                key: 'side',
                label: 'Сторона',
                default: 1,
                min: -1,
                max: 1,
                step: 2,
                choices: [
                    { value: 1, label: 'снизу, для покупок' },
                    { value: -1, label: 'сверху, для продаж' },
                ],
            },
            {
                key: 'size',
                label: 'Наименьший размер',
                default: 0.25,
                min: 0,
                max: 5,
                step: 0.05,
                unit: '× средний ход бара',
            },
        ],
        outputs: [
            { key: 'upper', label: 'Верх зоны' },
            { key: 'lower', label: 'Низ зоны' },
            { key: 'mid', label: 'Середина' },
            { key: 'active', label: 'Не закрыт', flag: true },
            { key: 'touched', label: 'Возврат в зону', flag: true },
        ],
    },
    {
        id: 'order_block',
        label: 'Ордер-блок',
        category: 'zone',
        hint: 'Последняя противоходная свеча перед импульсом',
        params: [
            {
                key: 'side',
                label: 'Сторона',
                default: 1,
                min: -1,
                max: 1,
                step: 2,
                choices: [
                    { value: 1, label: 'снизу, для покупок' },
                    { value: -1, label: 'сверху, для продаж' },
                ],
            },
            {
                key: 'strength',
                label: 'Наименьшая сила импульса',
                default: 1.5,
                min: 1,
                max: 5,
                step: 0.1,
                unit: '× средний ход бара',
            },
        ],
        outputs: [
            { key: 'upper', label: 'Верх зоны' },
            { key: 'lower', label: 'Низ зоны' },
            { key: 'mid', label: 'Середина' },
            { key: 'active', label: 'Активен', flag: true },
            { key: 'touched', label: 'Возврат в зону', flag: true },
        ],
    },
    {
        id: 'pivots',
        label: 'Пивот-уровни',
        category: 'zone',
        hint: 'Уровни из прошлых суток (на дневных — прошлой недели): от них часто отталкиваются',
        params: [],
        outputs: [
            { key: 'pp', label: 'Пивот' },
            { key: 'r1', label: 'Сопротивление 1' },
            { key: 's1', label: 'Поддержка 1' },
            { key: 'r2', label: 'Сопротивление 2' },
            { key: 's2', label: 'Поддержка 2' },
        ],
    },
    {
        id: 'premium',
        label: 'Премиум / дискаунт',
        category: 'structure',
        hint: 'Где цена внутри диапазона: 0 — у низа, 100 — у верха',
        params: [
            { key: 'left', label: 'Баров слева', default: 5, min: 1, max: 100, unit: 'баров' },
            { key: 'right', label: 'Баров справа', default: 5, min: 1, max: 100, unit: 'баров' },
        ],
    },
    /* ------------------------------------------------------------ свечи */
    {
        id: 'engulfing',
        label: 'Поглощение',
        category: 'candle',
        hint: 'Свеча перекрыла телом предыдущую и другого цвета: намерение сменилось',
        params: [
            {
                key: 'factor',
                label: 'Во сколько раз тело больше предыдущего',
                default: 1,
                min: 0.5,
                max: 5,
                step: 0.1,
            },
        ],
        outputs: [
            { key: 'up', label: 'Бычье', flag: true },
            { key: 'down', label: 'Медвежье', flag: true },
        ],
    },
    {
        id: 'pin_bar',
        label: 'Пин-бар',
        category: 'candle',
        hint: 'Длинная тень и прижатое тело: цену завели и вернули внутри свечи',
        params: [
            {
                key: 'factor',
                label: 'Во сколько раз тень длиннее тела',
                default: 2,
                min: 1,
                max: 10,
                step: 0.1,
            },
        ],
        outputs: [
            { key: 'up', label: 'Тень снизу', flag: true },
            { key: 'down', label: 'Тень сверху', flag: true },
        ],
    },
    {
        id: 'star',
        label: 'Утренняя / вечерняя звезда',
        category: 'candle',
        hint: 'Сильная свеча, маленькая, сильная обратная: разворот за три свечи',
        params: [],
        outputs: [
            { key: 'up', label: 'Утренняя', flag: true },
            { key: 'down', label: 'Вечерняя', flag: true },
        ],
    },
    {
        id: 'three_soldiers',
        label: 'Три солдата / три вороны',
        category: 'candle',
        hint: 'Три сильные свечи подряд в одну сторону: движение набрало ход',
        params: [],
        outputs: [
            { key: 'up', label: 'Три солдата', flag: true },
            { key: 'down', label: 'Три вороны', flag: true },
        ],
    },
    {
        id: 'inside_bar',
        label: 'Внутренний бар',
        category: 'candle',
        hint: 'Свеча целиком внутри предыдущей: сжатие перед движением',
        flag: true,
        params: [],
    },
    {
        id: 'doji',
        label: 'Доджи',
        category: 'candle',
        hint: 'Тело почти отсутствует: открытие и закрытие сошлись',
        flag: true,
        params: [
            {
                key: 'share',
                label: 'Наибольшая доля тела в размахе',
                default: 0.1,
                min: 0.01,
                max: 0.5,
                step: 0.01,
            },
        ],
    }
];
function seriesOf(bars, source) {
    return bars.map((bar) => bar[source]);
}
function numberOf(params, key, spec) {
    const raw = params[key];
    const value = typeof raw === 'number' ? raw : Number(raw);
    const fallback = spec?.default ?? 14;
    if (!Number.isFinite(value))
        return fallback;
    const min = spec?.min ?? 1;
    const max = spec?.max ?? 500;
    const clamped = Math.min(Math.max(value, min), max);
    // Периоды целые, множители — нет: дробный период не имеет смысла, а
    // округлённый множитель 2.5 превратился бы в другой индикатор.
    return spec?.step === undefined ? Math.floor(clamped) : clamped;
}
function paramsFor(id) {
    return RULE_INDICATORS.find((spec) => spec.id === id)?.params ?? [];
}
/**
 * Значения индикатора по ряду баров.
 *
 * Та же функция стоит и в терминале, и в Core: подменять её своей
 * реализацией нельзя — это ровно то расхождение, ради борьбы с которым
 * вычислитель сделали общим.
 */
/** Какой ряд зоны просят: границы, середина или сам факт наличия. */
function zoneOutput(zone, output) {
    if (output === 'lower')
        return zone.lower;
    if (output === 'mid')
        return zone.mid;
    if (output === 'active')
        return zone.active;
    if (output === 'touched')
        return zone.touched;
    return zone.upper;
}
/**
 * Скользящая сумма.
 *
 * Пусто, пока окно не заполнено, и пусто, если в окне есть бар без
 * данных: сумма по неполному окну выглядела бы настоящей.
 */
function rollingSum(values, period) {
    const size = Math.max(1, Math.floor(period));
    const out = [];
    let sum = 0;
    let missing = 0;
    for (let i = 0; i < values.length; i += 1) {
        const value = values[i] ?? null;
        if (value === null)
            missing += 1;
        else
            sum += value;
        if (i >= size) {
            const gone = values[i - size] ?? null;
            if (gone === null)
                missing -= 1;
            else
                sum -= gone;
        }
        out.push(i >= size - 1 && missing === 0 ? sum : null);
    }
    return out;
}
export const resolveIndicator = (id, params, output, bars, context) => {
    const specs = paramsFor(id);
    const param = (key) => numberOf(params, key, specs.find((spec) => spec.key === key));
    const closes = seriesOf(bars, 'close');
    const highs = seriesOf(bars, 'high');
    const lows = seriesOf(bars, 'low');
    const volumes = bars.map((bar) => bar.volume);
    const empty = () => bars.map(() => null);
    switch (id) {
        case 'sma':
            return sma(closes, param('period'));
        case 'ema':
            return ema(closes, param('period'));
        case 'wma':
            return wma(closes, param('period'));
        case 'rsi':
            return rsi(closes, param('period'));
        case 'atr':
            return atr(highs, lows, closes, param('period'));
        case 'stddev':
            return rollingStdDev(closes, param('period'));
        case 'cci':
            return cci(highs, lows, closes, param('period'));
        case 'adx':
            return adx(highs, lows, closes, param('period'));
        case 'roc':
            return roc(closes, param('period'));
        case 'vwap':
            return vwap(highs, lows, closes, volumes, param('period'));
        case 'obv':
            return obv(closes, volumes);
        case 'volume_sma':
            return sma(volumes, param('period'));
        case 'relative_volume':
            return relativeVolume(volumes, param('period'));
        case 'spot_cvd': {
            // Дельты нет — значение пустое, а не ноль: иначе условие «покупают
            // больше» молча не выполнялось бы там, где данных просто не было.
            const deltas = bars.map((bar) => typeof bar.spotDelta === 'number' && Number.isFinite(bar.spotDelta) ? bar.spotDelta : null);
            if (output === 'delta')
                return deltas;
            return rollingSum(deltas, param('period'));
        }
        case 'futures_cvd': {
            const deltas = bars.map((bar) => typeof bar.futuresDelta === 'number' && Number.isFinite(bar.futuresDelta)
                ? bar.futuresDelta
                : null);
            if (output === 'delta')
                return deltas;
            return rollingSum(deltas, param('period'));
        }
        /* ----------------------------------------------- структура рынка */
        case 'swing': {
            const pivots = swings(bars, param('left'), param('right'));
            return output === 'low' ? pivots.low : pivots.high;
        }
        case 'bos': {
            const s = structure(bars, param('left'), param('right'));
            return output === 'down' ? s.bosDown : s.bosUp;
        }
        case 'choch': {
            const s = structure(bars, param('left'), param('right'));
            return output === 'down' ? s.chochDown : s.chochUp;
        }
        case 'divergence': {
            /*
             * Импульс — RSI, и это сказано в подписи параметра, а не спрятано.
             * Дивергенцию считают и по MACD, и по объёму; выбор источника
             * добавится настройкой, когда конструктор научится списками —
             * числовой код «0 — RSI, 1 — MACD» человек читал бы как загадку.
             */
            const momentum = rsi(closes, param('period'));
            const found = divergences(bars, momentum, {
                left: param('left'),
                right: param('right'),
                maxGap: param('maxGap'),
            });
            switch (output) {
                case 'down':
                    return found.down;
                case 'hidden_up':
                    return found.hiddenUp;
                case 'hidden_down':
                    return found.hiddenDown;
                case 'triple_up':
                    return found.tripleUp;
                case 'triple_down':
                    return found.tripleDown;
                default:
                    return found.up;
            }
        }
        case 'trend_structure':
            return structure(bars, param('left'), param('right')).trend;
        case 'sweep': {
            const s = sweeps(bars, param('left'), param('right'));
            return output === 'low' ? s.low : s.high;
        }
        case 'fvg': {
            // Сторона задана числом: конструктор пока умеет только числовые
            // параметры, а заводить ради одного поля строковый — работа,
            // которая ничего не добавляет человеку.
            const zone = fairValueGaps(bars, param('side') < 0 ? 'down' : 'up', param('size'));
            return zoneOutput(zone, output);
        }
        case 'order_block': {
            const zone = orderBlocks(bars, param('side') < 0 ? 'down' : 'up', param('strength'));
            return zoneOutput(zone, output);
        }
        /* ------------------------------------------------------------ свечи */
        case 'engulfing': {
            const found = engulfing(bars, param('factor'));
            return output === 'down' ? found.down : found.up;
        }
        case 'pin_bar': {
            const found = pinBar(bars, param('factor'));
            return output === 'down' ? found.down : found.up;
        }
        case 'inside_bar':
            return insideBar(bars);
        case 'star': {
            const found = stars(bars);
            return output === 'down' ? found.down : found.up;
        }
        case 'three_soldiers': {
            const found = threeSoldiers(bars);
            return output === 'down' ? found.down : found.up;
        }
        /* ------------------------------------------------ вторая волна */
        case 'ichimoku': {
            const cloud = ichimoku(bars, param('tenkan'), param('kijun'), param('senkou'));
            switch (output) {
                case 'cloud_bottom':
                    return cloud.cloudBottom;
                case 'tenkan':
                    return cloud.tenkan;
                case 'kijun':
                    return cloud.kijun;
                case 'span_a':
                    return cloud.spanA;
                case 'span_b':
                    return cloud.spanB;
                default:
                    return cloud.cloudTop;
            }
        }
        case 'psar':
            return parabolicSar(bars, param('step'), param('max'));
        /*
         * Код ANZONIX: сам текст программы лежит в параметрах. Индикатор
         * пишет человек, но считается он здесь же — поэтому код работает
         * везде, где работает движок: на графике, на сервере, в ботах.
         */
        case 'script':
            return scriptSeries(params, output, bars, resolveIndicator, RULE_INDICATORS, context);
        case 'aroon': {
            const found = aroon(bars, param('period'));
            return output === 'down' ? found.down : output === 'oscillator' ? found.oscillator : found.up;
        }
        case 'williams_r':
            return williamsR(bars, param('period'));
        case 'keltner': {
            const channel = keltner(bars, param('period'), param('multiplier'), param('atrPeriod'));
            return output === 'lower' ? channel.lower : output === 'middle' ? channel.middle : channel.upper;
        }
        case 'donchian': {
            const channel = donchian(bars, param('period'));
            return output === 'lower' ? channel.lower : output === 'middle' ? channel.middle : channel.upper;
        }
        case 'mfi':
            return mfi(bars, param('period'));
        case 'pivots': {
            const levels = pivots(bars);
            return output === 'r1' ? levels.r1 : output === 's1' ? levels.s1 : output === 'r2' ? levels.r2 : output === 's2' ? levels.s2 : levels.pp;
        }
        case 'doji':
            return doji(bars, param('share'));
        case 'premium':
            return premium(bars, param('left'), param('right'));
        case 'supertrend':
            return supertrend(highs, lows, closes, param('period'), param('multiplier'));
        case 'macd': {
            const result = macd(closes, param('fast'), param('slow'), param('signal'));
            if (output === 'signal')
                return result.signal;
            if (output === 'histogram')
                return result.histogram;
            return result.line;
        }
        case 'stochastic': {
            const k = stochasticK(highs, lows, closes, param('period'));
            if (output !== 'd')
                return k;
            // %D — сглаженная %K. Пустые участки при этом остаются пустыми:
            // среднее по неизвестному значению не становится известным.
            const dense = [];
            const index = [];
            k.forEach((value, i) => {
                if (value === null)
                    return;
                dense.push(value);
                index.push(i);
            });
            const smoothed = sma(dense, param('smooth'));
            const out = empty();
            index.forEach((position, at) => {
                out[position] = smoothed[at] ?? null;
            });
            return out;
        }
        case 'bollinger': {
            const period = param('period');
            const deviations = param('deviations');
            const middle = sma(closes, period);
            if (output === 'middle')
                return middle;
            const deviation = rollingStdDev(closes, period);
            return middle.map((value, i) => {
                const spread = deviation[i];
                if (value === null || spread === null || spread === undefined)
                    return null;
                return output === 'lower' ? value - spread * deviations : value + spread * deviations;
            });
        }
        default:
            return empty();
    }
};
//# sourceMappingURL=indicators.js.map